/**
 * Valores monetários circulam como centavos inteiros dentro do domínio.
 *
 * O Prisma devolve `Decimal` e o navegador trabalha com `number`; converter nas
 * bordas para centavos evita que uma comparação de ponto flutuante decida o
 * score de uma Oportunidade em R$ 1.500,00 ou R$ 3.000,00.
 */
const CENTAVOS_POR_REAL = 100;
/**
 * Valor mensal que ninguém informou ainda.
 *
 * Zero, e não nulo: o valor é obrigatório no Contrato, na Oportunidade e na
 * Renovação, e cada leitura converte para centavos. Quem grava zero diz "não
 * sei"; a tela escreve tracinho. Usam a ingestão da Meta, antes da primeira
 * conversa, e o Contrato herdado, cujo valor da planilha envelheceu (ADR 0015).
 */
export const VALOR_MENSAL_DESCONHECIDO = 0;
/** Valor mensal em reais, ou tracinho quando é o desconhecido. */
export function formatarValorMensal(centavos) {
    return centavos === VALOR_MENSAL_DESCONHECIDO ? '—' : formatarReais(centavos);
}
export class ValorMonetarioInvalidoError extends Error {
    constructor(valor) {
        super(`Valor monetário inválido: ${String(valor)}`);
        this.name = 'ValorMonetarioInvalidoError';
    }
}
/** Converte "1500.5", "1.500,50", 1500.5 ou um Decimal do Prisma em centavos. */
export function paraCentavos(valor) {
    if (typeof valor === 'number') {
        if (!Number.isFinite(valor))
            throw new ValorMonetarioInvalidoError(valor);
        return Math.round(valor * CENTAVOS_POR_REAL);
    }
    const bruto = String(valor).trim();
    if (bruto === '')
        throw new ValorMonetarioInvalidoError(valor);
    // Aceita tanto "1500.50" (API/Prisma) quanto "1.500,50" (planilhas brasileiras).
    const temVirgulaDecimal = /,\d{1,2}$/.test(bruto);
    const normalizado = temVirgulaDecimal
        ? bruto.replace(/\./g, '').replace(',', '.')
        : bruto.replace(/,/g, '');
    if (!/^-?\d+(\.\d+)?$/.test(normalizado))
        throw new ValorMonetarioInvalidoError(valor);
    const [inteira = '0', decimal = ''] = normalizado.split('.');
    const negativo = inteira.startsWith('-');
    const centavosDecimais = Number(`${decimal}00`.slice(0, 2));
    const centavos = Math.abs(Number(inteira)) * CENTAVOS_POR_REAL + centavosDecimais;
    return negativo ? -centavos : centavos;
}
/** Converte centavos em uma string decimal aceita pelo Prisma (`"1500.50"`). */
export function centavosParaDecimal(centavos) {
    const negativo = centavos < 0;
    const absoluto = Math.abs(Math.round(centavos));
    const reais = Math.floor(absoluto / CENTAVOS_POR_REAL);
    const resto = absoluto % CENTAVOS_POR_REAL;
    return `${negativo ? '-' : ''}${reais}.${String(resto).padStart(2, '0')}`;
}
/** Formata centavos para exibição em português do Brasil (`"R$ 1.500,50"`). */
export function formatarReais(centavos) {
    return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
    }).format(centavos / CENTAVOS_POR_REAL);
}
/**
 * Formata centavos omitindo o `,00` de valores redondos (`"R$ 150.000"`).
 *
 * É a forma que os indicadores usam. Numa meta de seis dígitos, os centavos
 * zerados só ocupam a largura do card sem dizer nada — mas quando existem,
 * continuam aparecendo: `formatarReaisCompacto(849937)` é `"R$ 8.499,37"`.
 */
export function formatarReaisCompacto(centavos) {
    if (Math.round(centavos) % CENTAVOS_POR_REAL !== 0)
        return formatarReais(centavos);
    return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    }).format(centavos / CENTAVOS_POR_REAL);
}
//# sourceMappingURL=dinheiro.js.map